/**
 * Shopify Customer Account API login (OAuth 2.0 + PKCE, public SPA client).
 *
 * The React app runs the whole flow client-side and stores the resulting Shopify
 * **access token** under `authToken` — the same localStorage key the rest of the app
 * already uses as the backend Bearer token. The backend (Phase 1) introspects that token
 * via the Customer Account API to identify the customer.
 *
 * Config comes from `window.__ENV__` (injected at container start by env.sh).
 */

interface ShopifyEnv {
  VITE_SHOPIFY_CA_CLIENT_ID?: string;
  VITE_SHOPIFY_CA_SHOP_ID?: string;
  VITE_SHOPIFY_REDIRECT_URI?: string;
  VITE_SHOPIFY_SCOPES?: string;
}

const ACCESS_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'shopifyRefreshToken';
const TOKEN_EXPIRY_KEY = 'shopifyTokenExpiry';
const ID_TOKEN_KEY = 'shopifyIdToken';

const STATE_KEY = 'shopifyOauthState';
const VERIFIER_KEY = 'shopifyCodeVerifier';
const RETURN_TO_KEY = 'shopifyReturnTo';

function env(): ShopifyEnv {
  return ((window as any).__ENV__ ?? {}) as ShopifyEnv;
}

function config() {
  const e = env();
  const shopId = e.VITE_SHOPIFY_CA_SHOP_ID ?? '';
  const base = `https://shopify.com/authentication/${shopId}`;
  return {
    clientId: e.VITE_SHOPIFY_CA_CLIENT_ID ?? '',
    shopId,
    redirectUri: e.VITE_SHOPIFY_REDIRECT_URI ?? `${window.location.origin}/account/authorize`,
    scopes: e.VITE_SHOPIFY_SCOPES ?? 'openid email customer-account-api:full',
    authorizeUrl: `${base}/oauth/authorize`,
    tokenUrl: `${base}/oauth/token`,
    logoutUrl: `${base}/logout`,
  };
}

export function isShopifyConfigured(): boolean {
  const c = config();
  return Boolean(c.clientId && c.shopId);
}

// --- PKCE helpers ------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

// --- token storage -----------------------------------------------------------------

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function storeTokens(data: {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
}) {
  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  if (data.id_token) localStorage.setItem(ID_TOKEN_KEY, data.id_token);
  if (data.expires_in) {
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + data.expires_in * 1000));
  }
}

export function clearTokens() {
  [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRY_KEY, ID_TOKEN_KEY].forEach((k) =>
    localStorage.removeItem(k),
  );
}

// --- OAuth flow --------------------------------------------------------------------

/** Starts the login flow by redirecting to Shopify's hosted login/sign-up page. */
export async function beginLogin(returnTo?: string): Promise<void> {
  const c = config();
  const verifier = randomToken();
  const state = randomToken();
  const nonce = randomToken();
  const challenge = base64UrlEncode(await sha256(verifier));

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  const params = new URLSearchParams({
    client_id: c.clientId,
    response_type: 'code',
    redirect_uri: c.redirectUri,
    scope: c.scopes,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.href = `${c.authorizeUrl}?${params.toString()}`;
}

/**
 * Completes the flow on the callback route: validates state, exchanges the code for tokens.
 * Returns the path to navigate to afterwards.
 */
export async function handleCallback(): Promise<string> {
  const c = config();
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (error) {
    throw new Error(url.searchParams.get('error_description') || error);
  }
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !state || state !== expectedState || !verifier) {
    throw new Error('Ugyldig innloggingssvar fra Shopify');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: c.clientId,
    redirect_uri: c.redirectUri,
    code,
    code_verifier: verifier,
  });

  const res = await fetch(c.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    throw new Error(`Token-utveksling feilet (${res.status})`);
  }
  const data = await res.json();
  storeTokens(data);

  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/profil';
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo;
}

/** Refreshes the access token using the stored refresh token. Returns the new token or null. */
export async function refresh(): Promise<string | null> {
  const c = config();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: c.clientId,
    refresh_token: refreshToken,
  });
  try {
    const res = await fetch(c.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) return null;
    const data = await res.json();
    storeTokens(data);
    return data.access_token as string;
  } catch {
    return null;
  }
}

/** Clears local tokens. Pass redirectToShopify to also end the Shopify session. */
export function logout(redirectToShopify = false): void {
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  clearTokens();
  if (redirectToShopify) {
    const c = config();
    const params = new URLSearchParams({ post_logout_redirect_uri: window.location.origin });
    if (idToken) params.set('id_token_hint', idToken);
    window.location.href = `${c.logoutUrl}?${params.toString()}`;
  }
}
